// ═══════════════════════════════════════════════════════════════════════════
// Generador de códigos QR
//
// PARA QUÉ. El operador necesita dar de alta a alguien y firmarlo en una
// máquina cualquiera: "el dueño o administrador pueden generar un usuario y
// firmar al usuario con QR". La liga del ticket ya existía (`auth/ticket.js`,
// `#t=CODIGO`); esto la vuelve algo que se apunta con la cámara.
//
// POR QUÉ ESCRITO A MANO Y NO UNA LIBRERÍA. La app es una PWA que tiene que
// funcionar sin red y sus dependencias son tres: React, ReactDOM y el cliente
// de Supabase. Meter una cuarta para dibujar cuadritos no se paga solo.
//
// Y lo que hace defendible escribirlo es que se puede COMPROBAR: las pruebas
// generan códigos con este archivo y los comparan módulo por módulo contra
// `segno`, una implementación de referencia independiente. No se verifica que
// "se vea como un QR" —eso lo cumple cualquier ruido— sino que la matriz sea
// bit por bit la misma que produce alguien más a partir del estándar.
//
// ALCANCE. Modo binario (UTF-8), corrección M, versiones 1 a 10. Una liga de
// ticket ronda los 50 caracteres; la versión 10 aguanta 216 bytes. No se
// implementan los modos numérico ni alfanumérico: comprimirían más, pero cada
// modo es otra rama que probar y aquí no hacen falta.
//
// ISO/IEC 18004. Los nombres van en español salvo los del estándar, que se
// dejan como están para poder buscarlos.
// ═══════════════════════════════════════════════════════════════════════════

// ── Aritmética en GF(256), con el polinomio del estándar (0x11D) ───────────
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x; LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

const mul = (a, b) => (a === 0 || b === 0) ? 0 : EXP[LOG[a] + LOG[b]];

/** Polinomio generador de `n` codewords de corrección. */
function generador(n) {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const r = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      r[j] ^= g[j];                     // g · x
      r[j + 1] ^= mul(g[j], EXP[i]);    // g · α^i
    }
    g = r;
  }
  return g;
}

/** Los codewords de corrección de un bloque: el residuo de la división. */
function correccion(datos, n) {
  const g = generador(n);
  const buf = [...datos, ...new Array(n).fill(0)];
  for (let i = 0; i < datos.length; i++) {
    const c = buf[i];
    if (c === 0) continue;
    for (let j = 0; j < g.length; j++) buf[i + j] ^= mul(g[j], c);
  }
  return buf.slice(datos.length);
}

// ── Tablas del estándar, solo para corrección M ────────────────────────────
//
// Por versión: [codewords de datos, codewords de corrección POR BLOQUE,
//               [[cuántos bloques, codewords de datos en cada uno], …]]
const M = {
  1:  [16,  10, [[1, 16]]],
  2:  [28,  16, [[1, 28]]],
  3:  [44,  26, [[1, 44]]],
  4:  [64,  18, [[2, 32]]],
  5:  [86,  24, [[2, 43]]],
  6:  [108, 16, [[4, 27]]],
  7:  [124, 18, [[4, 31]]],
  8:  [154, 22, [[2, 38], [2, 39]]],
  9:  [182, 22, [[3, 36], [2, 37]]],
  10: [216, 26, [[4, 43], [1, 44]]],
};

/** Centros de los patrones de alineación, por versión. */
const ALINEACION = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
  6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
};

const MAX_VERSION = 10;

// ── Los datos, ya como bits ────────────────────────────────────────────────

/** La versión más chica en la que caben `n` bytes. */
function versionPara(n) {
  for (let v = 1; v <= MAX_VERSION; v++) {
    // El indicador de longitud son 8 bits hasta la versión 9 y 16 desde la 10.
    const cabecera = 4 + (v < 10 ? 8 : 16);
    if (M[v][0] * 8 >= cabecera + n * 8) return v;
  }
  throw new Error(
    `El texto no cabe en un QR versión ${MAX_VERSION} con corrección M ` +
    `(${n} bytes). Si hace falta más, hay que agregar las tablas de las ` +
    `versiones siguientes.`);
}

function bitsDeDatos(bytes, version) {
  const bits = [];
  const empujar = (valor, cuantos) => {
    for (let i = cuantos - 1; i >= 0; i--) bits.push((valor >> i) & 1);
  };

  empujar(0b0100, 4);                                  // modo binario
  empujar(bytes.length, version < 10 ? 8 : 16);        // cuántos bytes
  for (const b of bytes) empujar(b, 8);

  const capacidad = M[version][0] * 8;
  // Terminador: hasta cuatro ceros, o menos si ya no cabe.
  for (let i = 0; i < 4 && bits.length < capacidad; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  // Relleno alternando 11101100 / 00010001, como manda el estándar.
  const relleno = [0xec, 0x11];
  for (let i = 0; bits.length < capacidad; i++) empujar(relleno[i % 2], 8);

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    codewords.push(b);
  }
  return codewords;
}

/**
 * Parte los datos en bloques, calcula la corrección de cada uno y los
 * INTERCALA. El intercalado es lo que hace que una mancha en el papel dañe un
 * poco de cada bloque en vez de destruir uno entero.
 */
function codewordsFinales(datos, version) {
  const [, ecPorBloque, grupos] = M[version];
  const bloques = [];
  let i = 0;
  for (const [cuantos, tam] of grupos) {
    for (let k = 0; k < cuantos; k++) {
      const d = datos.slice(i, i + tam);
      i += tam;
      bloques.push({ datos: d, ec: correccion(d, ecPorBloque) });
    }
  }

  const salida = [];
  const maxDatos = Math.max(...bloques.map(b => b.datos.length));
  for (let c = 0; c < maxDatos; c++)
    for (const b of bloques) if (c < b.datos.length) salida.push(b.datos[c]);
  for (let c = 0; c < ecPorBloque; c++)
    for (const b of bloques) salida.push(b.ec[c]);
  return salida;
}

// ── La retícula ────────────────────────────────────────────────────────────

function conPatrones(version) {
  const n = 17 + 4 * version;
  const m = Array.from({ length: n }, () => new Array(n).fill(null));
  const fijo = Array.from({ length: n }, () => new Array(n).fill(false));

  const poner = (f, c, v) => { m[f][c] = v; fijo[f][c] = true; };

  // Los tres ojos, con su separador blanco.
  for (const [f0, c0] of [[0, 0], [0, n - 7], [n - 7, 0]]) {
    for (let f = -1; f <= 7; f++) {
      for (let c = -1; c <= 7; c++) {
        const ff = f0 + f, cc = c0 + c;
        if (ff < 0 || cc < 0 || ff >= n || cc >= n) continue;
        // El anillo de -1 y 7 es el SEPARADOR y va en blanco. Sin exigir que
        // f y c estén dentro del ojo, `f === 0` se cumplía también en la
        // columna 7 y pintaba de negro el separador: el lector veía un ojo
        // deforme. Lo encontró la comparación contra la referencia.
        const dentro = f >= 0 && f <= 6 && c >= 0 && c <= 6;
        const borde = dentro && (f === 0 || f === 6 || c === 0 || c === 6);
        const centro = f >= 2 && f <= 4 && c >= 2 && c <= 4;
        poner(ff, cc, borde || centro ? 1 : 0);
      }
    }
  }

  // Los tiempos: la fila y la columna 6, alternando.
  for (let i = 8; i < n - 8; i++) {
    const v = i % 2 === 0 ? 1 : 0;
    poner(6, i, v);
    poner(i, 6, v);
  }

  // Alineación, salvo donde chocaría con un ojo.
  const centros = ALINEACION[version];
  for (const f0 of centros) {
    for (const c0 of centros) {
      const enOjo = (f0 <= 8 && c0 <= 8) ||
                    (f0 <= 8 && c0 >= n - 9) ||
                    (f0 >= n - 9 && c0 <= 8);
      if (enOjo) continue;
      for (let f = -2; f <= 2; f++)
        for (let c = -2; c <= 2; c++)
          poner(f0 + f, c0 + c,
                (Math.abs(f) === 2 || Math.abs(c) === 2 || (f === 0 && c === 0)) ? 1 : 0);
    }
  }

  // El módulo que siempre está negro.
  poner(4 * version + 9, 8, 1);

  // Se reservan —sin valor todavía— los lugares del formato…
  const reservar = (f, c) => { fijo[f][c] = true; };
  for (let i = 0; i <= 8; i++) { if (i !== 6) { reservar(8, i); reservar(i, 8); } }
  for (let i = 0; i < 8; i++) { reservar(8, n - 1 - i); reservar(n - 1 - i, 8); }
  // …y los de la versión, que solo existen de la 7 en adelante.
  if (version >= 7) {
    for (let i = 0; i < 6; i++)
      for (let j = 0; j < 3; j++) { reservar(i, n - 11 + j); reservar(n - 11 + j, i); }
  }

  return { m, fijo, n };
}

/** Recorre en zigzag de derecha a izquierda, saltándose la columna 6. */
function colocarDatos(m, fijo, n, codewords) {
  let bit = 0;
  const siguiente = () => {
    const i = bit >> 3, j = 7 - (bit & 7);
    bit++;
    return i < codewords.length ? (codewords[i] >> j) & 1 : 0;
  };

  let arriba = true;
  for (let c = n - 1; c > 0; c -= 2) {
    if (c === 6) c--;                       // la columna del tiempo no cuenta
    for (let paso = 0; paso < n; paso++) {
      const f = arriba ? n - 1 - paso : paso;
      for (const cc of [c, c - 1]) {
        if (fijo[f][cc]) continue;
        m[f][cc] = siguiente();
      }
    }
    arriba = !arriba;
  }
}

const MASCARAS = [
  (f, c) => (f + c) % 2 === 0,
  (f) => f % 2 === 0,
  (f, c) => c % 3 === 0,
  (f, c) => (f + c) % 3 === 0,
  (f, c) => (Math.floor(f / 2) + Math.floor(c / 3)) % 2 === 0,
  (f, c) => ((f * c) % 2) + ((f * c) % 3) === 0,
  (f, c) => (((f * c) % 2) + ((f * c) % 3)) % 2 === 0,
  (f, c) => (((f + c) % 2) + ((f * c) % 3)) % 2 === 0,
];

/**
 * Las cuatro penalizaciones del estándar. Se elige la máscara con la nota más
 * baja: la que deja el dibujo menos parecido a los patrones de referencia, que
 * es lo que confunde a un lector.
 */
function penalizacion(m, n) {
  let p = 0;

  // 1 · rachas de cinco o más del mismo color
  for (let i = 0; i < n; i++) {
    for (const leer of [(k) => m[i][k], (k) => m[k][i]]) {
      let racha = 1;
      for (let k = 1; k < n; k++) {
        if (leer(k) === leer(k - 1)) racha++;
        else { if (racha >= 5) p += racha - 2; racha = 1; }
      }
      if (racha >= 5) p += racha - 2;
    }
  }

  // 2 · bloques de 2×2
  for (let f = 0; f < n - 1; f++)
    for (let c = 0; c < n - 1; c++)
      if (m[f][c] === m[f][c + 1] && m[f][c] === m[f + 1][c] &&
          m[f][c] === m[f + 1][c + 1]) p += 3;

  // 3 · el patrón que se parece a un ojo
  const A = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const B = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
  const igual = (leer, k, patron) => patron.every((v, i) => leer(k + i) === v);
  for (let i = 0; i < n; i++) {
    for (const leer of [(k) => m[i][k], (k) => m[k][i]]) {
      for (let k = 0; k + 11 <= n; k++)
        if (igual(leer, k, A) || igual(leer, k, B)) p += 40;
    }
  }

  // 4 · desequilibrio entre negro y blanco
  let negros = 0;
  for (let f = 0; f < n; f++) for (let c = 0; c < n; c++) negros += m[f][c];
  const porciento = (negros * 100) / (n * n);
  p += Math.floor(Math.abs(porciento - 50) / 5) * 10;

  return p;
}

/** Los 15 bits del formato: nivel M (00) + máscara, con su BCH y su XOR. */
function bitsDeFormato(mascara) {
  let v = (0b00 << 3) | mascara;          // M = 00
  let resto = v << 10;
  for (let i = 4; i >= 0; i--)
    if ((resto >> (i + 10)) & 1) resto ^= 0b10100110111 << i;
  return ((v << 10) | resto) ^ 0b101010000010010;
}

/** Los 18 bits de la versión, de la 7 en adelante. */
function bitsDeVersion(version) {
  let resto = version << 12;
  for (let i = 5; i >= 0; i--)
    if ((resto >> (i + 12)) & 1) resto ^= 0b1111100100101 << i;
  return (version << 12) | resto;
}

/**
 * Los 15 bits del formato, en sus dos copias.
 *
 * Se cuentan DESDE EL MÁS SIGNIFICATIVO (`bit(0)` es el de más peso). Es la
 * numeración del estándar y no la natural en JavaScript, y por eso se nombra:
 * la primera versión usaba `(f >> i) & 1` —del menos significativo— y escribía
 * los 15 bits **al revés**.
 *
 * Costó encontrarlo porque el código salía casi idéntico al de la referencia
 * (12 módulos de diferencia en una matriz de 3,249) y aun así NINGÚN lector lo
 * leía: sin el formato correcto, un decodificador no sabe qué máscara quitar,
 * así que ni siquiera empieza. De ahí la lección: el parecido no es la prueba,
 * la prueba es que se lea.
 */
function ponerFormato(m, n, mascara) {
  const f = bitsDeFormato(mascara);
  const bit = (k) => (f >> (14 - k)) & 1;

  // Copia 1: alrededor del ojo de arriba a la izquierda.
  for (let k = 0; k <= 5; k++)  m[8][k] = bit(k);
  m[8][7] = bit(6);
  m[8][8] = bit(7);
  m[7][8] = bit(8);
  for (let k = 9; k <= 14; k++) m[14 - k][8] = bit(k);

  // Copia 2: se reparte 7 + 8, no 8 + 7. Escribirla 8 + 7 dejaba la celda
  // (8, n−8) sin asignar, y un módulo nulo se dibuja blanco: el código salía
  // "casi bien", que es la peor manera de estar mal.
  for (let k = 0; k <= 6; k++)  m[n - 1 - k][8] = bit(k);
  for (let k = 7; k <= 14; k++) m[8][n - 15 + k] = bit(k);
  m[n - 8][8] = 1;                         // el módulo siempre negro
}

function ponerVersion(m, n, version) {
  if (version < 7) return;
  const v = bitsDeVersion(version);
  for (let i = 0; i < 18; i++) {
    const b = (v >> i) & 1;
    const f = Math.floor(i / 3), c = i % 3;
    m[f][n - 11 + c] = b;
    m[n - 11 + c][f] = b;
  }
}

/**
 * La matriz de un QR, como filas de 0 y 1.
 *
 * No lleva el margen blanco (la "zona tranquila"): eso es cosa de quien lo
 * dibuje, y va en `svgDeQR`.
 */
export function matrizQR(texto, { mascaraForzada = null } = {}) {
  const bytes = [...new TextEncoder().encode(String(texto ?? ""))];
  const version = versionPara(bytes.length);
  const codewords = codewordsFinales(bitsDeDatos(bytes, version), version);

  // `mascaraForzada` existe para poder COMPARAR contra una implementación de
  // referencia. El estándar no obliga a una máscara concreta —cualquiera de las
  // ocho da un código legible— así que dos implementaciones correctas pueden
  // producir dibujos distintos. Fijando la máscara en las dos, la comparación
  // deja de medir el gusto de cada una y mide lo que importa: codificación,
  // corrección, intercalado, colocación y bits de formato.
  const desde = mascaraForzada ?? 0;
  const hasta = mascaraForzada ?? 7;

  let mejor = null;
  for (let mascara = desde; mascara <= hasta; mascara++) {
    const { m, fijo, n } = conPatrones(version);
    colocarDatos(m, fijo, n, codewords);
    for (let f = 0; f < n; f++)
      for (let c = 0; c < n; c++)
        if (!fijo[f][c] && MASCARAS[mascara](f, c)) m[f][c] ^= 1;
    ponerFormato(m, n, mascara);
    ponerVersion(m, n, version);
    const nota = penalizacion(m, n);
    if (!mejor || nota < mejor.nota) mejor = { m, nota };
  }

  // Ningún módulo puede quedar sin decidir. Vale la pena comprobarlo aquí
  // porque un `null` no se ve: al dibujarlo cuenta como blanco y el código sale
  // "casi bien", que es la peor manera de estar mal. La primera versión dejaba
  // uno suelto y se delató por una fila más corta al imprimirla.
  for (const fila of mejor.m)
    if (fila.some(v => v !== 0 && v !== 1))
      throw new Error("QR incompleto: quedó un módulo sin asignar.");

  return mejor.m;
}

/**
 * El QR como SVG, listo para meter en un `src` o en el DOM.
 *
 * `margen` en módulos: el estándar pide 4 y menos que eso hace que algunos
 * lectores fallen justo cuando el código está pegado a otra cosa.
 */
export function svgDeQR(texto, { modulo = 6, margen = 4, color = "#000", fondo = "#fff" } = {}) {
  const m = matrizQR(texto);
  const n = m.length;
  const lado = (n + margen * 2) * modulo;

  // Un solo `path` en vez de un rect por módulo: son cientos, y así el SVG
  // pesa una fracción y el navegador no arma cientos de nodos.
  let d = "";
  for (let f = 0; f < n; f++)
    for (let c = 0; c < n; c++)
      if (m[f][c])
        d += `M${(c + margen) * modulo} ${(f + margen) * modulo}h${modulo}v${modulo}h-${modulo}z`;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${lado}" height="${lado}" ` +
         `viewBox="0 0 ${lado} ${lado}" shape-rendering="crispEdges">` +
         `<rect width="${lado}" height="${lado}" fill="${fondo}"/>` +
         `<path d="${d}" fill="${color}"/></svg>`;
}

/** El mismo SVG como `data:` URI, para usarlo directo en un `<img src>`. */
export const qrComoImagen = (texto, opciones) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(svgDeQR(texto, opciones))}`;
